import _ from 'lodash';
import d3 from 'd3';
import PropTypes from 'prop-types';
import React from 'react';

import AgentTable from './lib/agent_table';
import { CardRow, NumberCard } from 'components/liveboards/cards/metric_card';
import connect from 'components/lib/connect';
import { findReportConfigBySlug } from 'models/reports/report';
import { getDatesInRange } from 'models/location/reports';
import { LegacyReportConfigs } from 'scripts/domain/models/reports/report_configs';
import ReportChannel from 'models/reports/report_channel';
import ReportInfo from '../report_info';

export function AgentReport({
  aggregationLevel,
  allResponses,
  channel,
  days,
  endAt,
  initializedReport,
  responseLabel,
  startAt,
  windowWidth,
}) {
  const data = allResponses.length === 0 ? [0] : allResponses;

  const sumStat = _.sum(data);
  const avgStat = sumStat / data.length;

  return (
    <div>
      <ReportInfo startDate={startAt} endDate={endAt}>
        <CardRow>
          <NumberCard label={responseLabel} value={sumStat} />
          <NumberCard label="Average" value={avgStat} decimalPlaces={1} />
          <NumberCard label="Median" value={d3.quantile(data, 0.5)} decimalPlaces={1} />
          <NumberCard label="25th Percentile" value={d3.quantile(data, 0.25)} decimalPlaces={1} />
          <NumberCard label="75th Percentile" value={d3.quantile(data, 0.75)} decimalPlaces={1} />
        </CardRow>
      </ReportInfo>
      <AgentTable
        days={days}
        report={initializedReport}
        windowWidth={windowWidth}
        highlight={{
          // the first three columns are:
          // 1. the agent's name
          // 2. their total responses
          // 3. the sparkline
          firstCol: 3,
          high: d3.quantile(allResponses, 0.75),
          low: d3.quantile(allResponses, 0.25),
        }}
        aggregationLevel={aggregationLevel}
        channel={channel}
      />
    </div>
  );
}

AgentReport.propTypes = {
  aggregationLevel: PropTypes.string,
  allResponses: PropTypes.arrayOf(PropTypes.number),
  channel: PropTypes.string,
  days: PropTypes.arrayOf(PropTypes.string),
  endAt: PropTypes.string,
  initializedReport: PropTypes.object,
  responseLabel: PropTypes.string,
  startAt: PropTypes.string,
  windowWidth: PropTypes.number,
};

function mapStateToProps(context, { report, channel, windowWidth }) {
  function getResponseLabel(responseChannel) {
    switch (responseChannel) {
      case ReportChannel.EMAIL:
        return 'Total Outgoing Emails';
      case ReportChannel.PHONE_CALL:
        return 'Total Calls';
      case ReportChannel.SMS:
        return 'Total Outgoing SMSs';
      case ReportChannel.FB_MESSENGER:
        return 'Total Outgoing FB Messages';
      case ReportChannel.CHAT_SESSION:
        return 'Total Chats';
      default:
        return 'Total Responses';
    }
  }

  const currentLocation = context.getProvider('currentLocation').get();

  const responseLabel = getResponseLabel(channel);
  const startAt = currentLocation.getStartAt();
  const endAt = currentLocation.getEndAt();
  const aggregationLevel = currentLocation.getAggregationLevel(
    findReportConfigBySlug(LegacyReportConfigs, currentLocation.getSlug()).timeAggregation
  );

  const initializedReport = report.init();
  const days = getDatesInRange(startAt, endAt, aggregationLevel);
  const agentIds = initializedReport.getIds();

  const allResponses = _.chain(agentIds)
    .map(agentId => _.values(initializedReport.getDataForId(agentId)))
    .flatten()
    .sort((a, b) => a - b)
    .value();

  return {
    allResponses,
    aggregationLevel,
    channel,
    days,
    endAt,
    initializedReport,
    responseLabel,
    startAt,
    windowWidth,
  };
}

const AgentReportContainer = connect(mapStateToProps)(AgentReport);

export default AgentReportContainer;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/reporting/report/agent_report.jsx